/**
 * StorageManager - Safe wrapper around localStorage with error handling
 *
 * Handles common localStorage issues:
 * - Private browsing mode
 * - Quota exceeded
 * - SecurityError in cross-origin contexts
 */
export class StorageManager {
  constructor() {
    this._available = this._checkAvailability();
  }

  /**
   * Check if localStorage is available
   * @returns {boolean}
   * @private
   */
  _checkAvailability() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get item from localStorage
   * @param {string} key
   * @param {*} defaultValue - Value to return if key doesn't exist or error occurs
   * @returns {string|null|*}
   */
  getItem(key, defaultValue = null) {
    if (!this._available) return defaultValue;

    try {
      return localStorage.getItem(key) || defaultValue;
    } catch (e) {
      console.warn(`StorageManager: Failed to get item '${key}':`, e);
      return defaultValue;
    }
  }

  /**
   * Set item in localStorage
   * @param {string} key
   * @param {string} value
   * @returns {boolean} Success status
   */
  setItem(key, value) {
    if (!this._available) return false;

    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn(`StorageManager: Failed to set item '${key}':`, e);
      return false;
    }
  }

  /**
   * Remove item from localStorage
   * @param {string} key
   * @returns {boolean} Success status
   */
  removeItem(key) {
    if (!this._available) return false;

    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.warn(`StorageManager: Failed to remove item '${key}':`, e);
      return false;
    }
  }

  /**
   * Check if localStorage is available
   * @returns {boolean}
   */
  isAvailable() {
    return this._available;
  }

  /**
   * Get boolean value from localStorage
   * @param {string} key
   * @param {boolean} defaultValue
   * @returns {boolean}
   */
  getBoolean(key, defaultValue = false) {
    const value = this.getItem(key);
    if (value === null) return defaultValue;
    return value === 'true';
  }

  /**
   * Set boolean value in localStorage
   * @param {string} key
   * @param {boolean} value
   * @returns {boolean} Success status
   */
  setBoolean(key, value) {
    return this.setItem(key, value ? 'true' : 'false');
  }
}

// Singleton instance for convenience
export const storageManager = new StorageManager();
